import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";

import type { AttentionReason } from "./use-orders";

type ReviewChoice = components["schemas"]["ReviewChoice"];
export type ReviewChoicesByReason = Partial<Record<AttentionReason, ReviewChoice[]>>;

/**
 * GET /v1/orders/review-choices — static reference of the operator-selectable
 * resolution choices per post-ingest review reason. Never changes at
 * runtime, so it's cached forever (staleTime Infinity).
 */
export function useReviewChoices(): UseQueryResult<ReviewChoicesByReason, ApiError> {
  return useQuery({
    queryKey: ["orders", "review-choices"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/orders/review-choices", {});
      if (error) throw error;
      return data as ReviewChoicesByReason;
    },
    staleTime: Infinity,
  });
}

export interface ResolveReasonVars {
  id: string;
  reason: AttentionReason;
  choice: string;
  note?: string;
}

/**
 * POST /v1/orders/{id}/review-reasons/{reason}/resolve — records an
 * operator's decision. Never changes stock or lifecycle; only clears the
 * flag (and, for buyer_cancel_requested, lifts the hold). On success
 * invalidates every query under ["order"] plus the attention-counts widget.
 */
export function useResolveReason(): UseMutationResult<void, ApiError, ResolveReasonVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, ResolveReasonVars>({
    mutationFn: async ({ id, reason, choice, note }: ResolveReasonVars) => {
      const { error } = await apiClient.POST("/v1/orders/{id}/review-reasons/{reason}/resolve", {
        params: { path: { id, reason } },
        body: { choice, ...(note ? { note } : {}) },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["order"] });
      // The list is keyed ["orders", filter] — a separate root from ["order"],
      // so it must be invalidated on its own or the row keeps its flag (I1).
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
